import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { FormField } from "@/components/FormField";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  PROGRAMS, GENERAL_CONTACT, GENERAL_BACKGROUND, CDL_FIELDS, DISPATCHER_FIELDS, WGU_FIELDS,
} from "@/data/intakeConfig";
import { ArrowLeft, ArrowRight, Check, Truck, Headset, GraduationCap, Users, Send, Save, Copy } from "lucide-react";

const PROGRAM_ICONS = { 0: Truck, 1: Headset, 2: GraduationCap, 3: Users };
const CDL_ID = "Class A CDL through 160 Driving Academy";
const DISPATCH_ID = "AAWTA Certified Dispatcher Program";
const WGU_ID = "WGU Behind-the-Desk Certificate";

const visible = (fields, data) =>
  fields.filter((f) => !f.showIf || data[f.showIf.field] === f.showIf.value);

export default function Intake() {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [general, setGeneral] = useState({});
  const [cdl, setCdl] = useState({});
  const [dispatcher, setDispatcher] = useState({});
  const [wgu, setWgu] = useState({});
  const [consents, setConsents] = useState({});
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [resumeToken, setResumeToken] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);

  // Hydrate from a saved-and-return link (?resume=TOKEN)
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("resume");
    if (!token) return;
    api.get(`/intake/draft/${token}`)
      .then((r) => {
        const d = r.data || {};
        if (d.programs) setPrograms(d.programs);
        if (d.general) setGeneral(d.general);
        if (d.cdl) setCdl(d.cdl);
        if (d.dispatcher) setDispatcher(d.dispatcher);
        if (d.wgu) setWgu(d.wgu);
        if (d.consents) setConsents(d.consents);
        if (typeof d.step === "number") setStep(d.step);
        setResumeToken(token);
        toast.success("Welcome back — your saved intake was restored.");
      })
      .catch(() => toast.error("We couldn't find that saved intake."));
  }, []);

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      const res = await api.post("/intake/draft", { resume_token: resumeToken, programs, general, cdl, dispatcher, wgu, consents, step });
      const token = res.data.resume_token;
      setResumeToken(token);
      const link = `${window.location.origin}/intake?resume=${token}`;
      try { await navigator.clipboard.writeText(link); } catch { /* ignore */ }
      toast.success("Progress saved! Resume link copied to your clipboard.", { description: link, duration: 8000 });
    } catch {
      toast.error("Could not save your progress. Please try again.");
    } finally {
      setSavingDraft(false);
    }
  };

  // Build the dynamic list of steps
  const steps = useMemo(() => {
    const s = [
      { key: "programs", title: "Select Your Pathways" },
      { key: "general_contact", title: "Applicant Information" },
      { key: "general_background", title: "Background & Support Needs" },
    ];
    if (programs.includes(CDL_ID)) s.push({ key: "cdl", title: "CDL Pre-Qualification" });
    if (programs.includes(DISPATCH_ID)) s.push({ key: "dispatcher", title: "Dispatcher Qualification" });
    if (programs.includes(WGU_ID)) s.push({ key: "wgu", title: "WGU Qualification" });
    s.push({ key: "review", title: "Review & Submit" });
    return s;
  }, [programs]);

  const total = steps.length;
  const current = steps[Math.min(step, total - 1)];
  const percent = Math.round(((step + 1) / total) * 100);

  const setters = {
    general_contact: [general, setGeneral],
    general_background: [general, setGeneral],
    cdl: [cdl, setCdl],
    dispatcher: [dispatcher, setDispatcher],
    wgu: [wgu, setWgu],
  };

  const fieldSet = (key) => ({
    general_contact: visible(GENERAL_CONTACT, general),
    general_background: visible(GENERAL_BACKGROUND, general),
    cdl: visible(CDL_FIELDS, cdl),
    dispatcher: visible(DISPATCHER_FIELDS, dispatcher),
    wgu: visible(WGU_FIELDS, wgu),
  }[key] || []);

  const toggleProgram = (id) => {
    setPrograms((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  };

  const onChange = (setter, data) => (name, value) => {
    setter({ ...data, [name]: value });
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const validateStep = () => {
    if (current.key === "programs") {
      if (programs.length === 0) { toast.error("Please select at least one pathway."); return false; }
      return true;
    }
    if (current.key === "review") return true;
    const fields = fieldSet(current.key);
    const [data] = setters[current.key];
    const newErr = {};
    fields.forEach((f) => {
      if (f.required && (!data[f.name] || String(data[f.name]).trim() === "")) {
        newErr[f.name] = "This field is required. Enter \"None\" if it does not apply.";
      }
      if (f.type === "email" && data[f.name] && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data[f.name])) {
        newErr[f.name] = "Enter a valid email address.";
      }
    });
    setErrors(newErr);
    if (Object.keys(newErr).length) { toast.error("Please complete the required fields."); return false; }
    return true;
  };

  const next = () => { if (validateStep()) { setStep((s) => Math.min(s + 1, total - 1)); window.scrollTo(0, 0); } };
  const back = () => { setStep((s) => Math.max(s - 1, 0)); window.scrollTo(0, 0); };

  const submit = async () => {
    // consent checks
    if (programs.includes(CDL_ID) && !consents.cdl_consent) { toast.error("CDL consent is required."); return; }
    if (programs.includes(WGU_ID) && !consents.wgu_consent) { toast.error("WGU consent is required."); return; }
    if (!consents.privacy_consent) { toast.error("Please accept the privacy and consent statement."); return; }
    setSubmitting(true);
    try {
      const payload = {
        general, programs, consents,
        cdl: programs.includes(CDL_ID) ? cdl : null,
        dispatcher: programs.includes(DISPATCH_ID) ? dispatcher : null,
        wgu: programs.includes(WGU_ID) ? wgu : null,
      };
      const res = await api.post("/intake", payload);
      navigate("/confirmation", { state: res.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App bg-navy-light min-h-screen">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Progress */}
        <div className="mb-8" data-testid="intake-progress">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-display font-bold text-navy">Step {step + 1} of {total}</span>
            <span className="font-semibold text-magenta">{percent}% Complete</span>
          </div>
          <div className="bg-navy/15 h-2 rounded-full w-full">
            <div className="bg-magenta h-2 rounded-full transition-all duration-300" style={{ width: `${percent}%` }} />
          </div>
          <p className="mt-3 font-display text-2xl font-bold text-navy">{current.title}</p>
        </div>

        <div className="bg-white rounded-lg border border-navy/10 shadow-sm p-6 md:p-8 animate-fade-up" key={current.key}>
          {current.key === "programs" && (
            <div className="space-y-3">
              <p className="text-dark-text/70 text-sm mb-4">Select one or more pathways. You'll only be asked the questions connected to your choices.</p>
              {PROGRAMS.map((p, i) => {
                const Icon = PROGRAM_ICONS[i];
                const active = programs.includes(p.id);
                return (
                  <button type="button" key={p.id} onClick={() => toggleProgram(p.id)}
                    data-testid={`intake-program-${i}`}
                    className={`w-full text-left flex items-start gap-4 p-4 rounded-lg border-2 transition-all ${active ? "border-magenta bg-magenta-light" : "border-navy/10 bg-white hover:border-navy/30"}`}>
                    <span className={`w-11 h-11 rounded-md flex items-center justify-center shrink-0 ${active ? "bg-magenta text-white" : "bg-navy-light text-navy"}`}>
                      <Icon size={22} />
                    </span>
                    <span className="flex-1">
                      <span className="font-display font-bold text-navy block">{p.title}</span>
                      <span className="text-sm text-dark-text/70">{p.desc}</span>
                    </span>
                    <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${active ? "bg-magenta border-magenta" : "border-navy/20"}`}>
                      {active && <Check size={14} className="text-white" />}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {["general_contact", "general_background", "cdl", "dispatcher", "wgu"].includes(current.key) && (
            <>
              {current.key === "cdl" && (
                <div className="mb-5 bg-navy-light border border-navy/10 rounded-md p-4 text-sm text-navy">
                  This questionnaire provides preliminary readiness information. <strong>160 Driving Academy makes final admission and funding decisions.</strong>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {fieldSet(current.key).map((f) => {
                  const [data, setter] = setters[current.key];
                  return <FormField key={f.name} field={f} value={data[f.name]} error={errors[f.name]} onChange={onChange(setter, data)} />;
                })}
              </div>
            </>
          )}

          {current.key === "review" && (
            <div className="space-y-6" data-testid="intake-review">
              <div>
                <h4 className="font-display font-bold text-navy mb-2">Selected Pathways</h4>
                <div className="flex flex-wrap gap-2">
                  {programs.map((p) => <span key={p} className="bg-magenta-light text-magenta border border-magenta/20 text-xs font-bold px-3 py-1 rounded-full">{p}</span>)}
                </div>
              </div>
              <div className="text-sm text-dark-text/80">
                <p><strong>{general.first_name} {general.last_name}</strong> · {general.email} · {general.phone}</p>
              </div>

              <div className="space-y-3 pt-2 border-t border-navy/10">
                {programs.includes(CDL_ID) && (
                  <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="consent-cdl">
                    <input type="checkbox" className="mt-1 accent-magenta w-4 h-4" checked={!!consents.cdl_consent}
                      onChange={(e) => setConsents({ ...consents, cdl_consent: e.target.checked })} />
                    <span>I authorize AAWTA to review my information and refer me to 160 Driving Academy if approved.</span>
                  </label>
                )}
                {programs.includes(WGU_ID) && (
                  <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="consent-wgu">
                    <input type="checkbox" className="mt-1 accent-magenta w-4 h-4" checked={!!consents.wgu_consent}
                      onChange={(e) => setConsents({ ...consents, wgu_consent: e.target.checked })} />
                    <span>I authorize AAWTA to share my submitted information with WGU if AAWTA approves me for referral.</span>
                  </label>
                )}
                <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="consent-privacy">
                  <input type="checkbox" className="mt-1 accent-magenta w-4 h-4" checked={!!consents.privacy_consent}
                    onChange={(e) => setConsents({ ...consents, privacy_consent: e.target.checked })} />
                  <span>I understand that final program approval requires AAWTA and partner review, and that acceptance, tuition, funding, employment, or CDL licensing is not guaranteed. I consent to AAWTA storing my information per its privacy policy.</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Nav buttons */}
        <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
          <button onClick={back} disabled={step === 0} data-testid="intake-back"
            className="inline-flex items-center gap-2 py-3 px-6 rounded-md font-bold border-2 border-navy text-navy disabled:opacity-30 hover:bg-navy-light transition-colors">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-3">
            <button onClick={saveDraft} disabled={savingDraft} data-testid="intake-save-draft"
              className="inline-flex items-center gap-2 py-3 px-5 rounded-md font-bold border-2 border-magenta text-magenta hover:bg-magenta-light transition-colors disabled:opacity-60">
              {savingDraft ? "Saving…" : <><Save size={16} /> Save & Finish Later</>}
            </button>
            {current.key !== "review" ? (
              <button onClick={next} data-testid="intake-continue"
                className="inline-flex items-center gap-2 py-3 px-7 rounded-md font-bold bg-magenta hover:bg-magenta-dark text-white transition-colors">
                Continue <ArrowRight size={16} />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting} data-testid="intake-submit"
                className="inline-flex items-center gap-2 py-3 px-7 rounded-md font-bold bg-magenta hover:bg-magenta-dark text-white transition-colors disabled:opacity-60">
                {submitting ? "Submitting…" : <>Submit Intake <Send size={16} /></>}
              </button>
            )}
          </div>
        </div>
        {resumeToken && (
          <div className="mt-3 bg-navy-light border border-navy/20 rounded-md p-3" data-testid="resume-link-box">
            <p className="text-xs font-bold text-navy mb-1">Your resume link — save it to finish later:</p>
            <div className="flex items-center gap-2">
              <input readOnly value={`${window.location.origin}/intake?resume=${resumeToken}`}
                onFocus={(e) => e.target.select()}
                className="flex-1 bg-white border border-navy/20 rounded px-2 py-1.5 text-xs font-mono text-navy outline-none" />
              <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/intake?resume=${resumeToken}`); toast.success("Copied"); }}
                className="inline-flex items-center gap-1 font-bold text-white bg-magenta hover:bg-magenta-dark rounded px-3 py-1.5 text-xs shrink-0"><Copy size={13} /> Copy</button>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
